/* eslint-disable max-lines-per-function */
import { FieldBase } from '@/components/form';
import { useAnlageassistentFormContext } from '@/domain/mitarbeiter/features/anlageassistent/context/AnlageassistentFormContext';
import {
  type Arbeitstage,
  arbeitstage,
} from '@/domain/mitarbeiter/features/anlageassistent/utils/constants/tage';
import { mapArbeitstageSoll } from '@/domain/mitarbeiter/features/anlageassistent/utils/mapArbeitstageSoll';

import {
  Button,
  Drawer,
  DrawerContent,
  DrawerTrigger,
  ScrollArea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@e2n/ui';
import { faAngleDown, faCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export function ArbeitstageSelect({
  hideOptionFlexibel = false,
}: {
  hideOptionFlexibel?: boolean;
}) {
  const { t } = useTranslation();
  const form = useAnlageassistentFormContext();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const arbeitstageSelected =
    form.state.values.bezahlung.arbeitszeitkonto.gehaltsempfaenger.arbeitstage
      .length > 0;

  useEffect(() => {
    if (arbeitstageSelected) {
      form.setFieldValue(
        'abwesenheiten.allgemein.anzahlArbeitstage',
        mapArbeitstageSoll(
          form.state.values.bezahlung.arbeitszeitkonto.gehaltsempfaenger
            .arbeitstage,
        ),
      );
    }
  }, [arbeitstageSelected, form]);

  let options = Object.keys(arbeitstage);

  if (hideOptionFlexibel) {
    options = options.filter(
      (val) => (val as keyof Arbeitstage) !== 'FLEXIBEL',
    );
  }

  return (
    <div className="grid md:grid-cols-2">
      <div className="flex flex-col gap-1">
        <form.AppField name="abwesenheiten.allgemein.anzahlArbeitstage">
          {(field) => (
            <FieldBase
              label={t(
                'domain.mitarbeiter.create.steps.abwesenheiten.allgemein.form.arbeitstage.label',
              )}
              isRequired>
              <Select
                disabled={arbeitstageSelected}
                value={
                  arbeitstageSelected
                    ? mapArbeitstageSoll(
                        form.state.values.bezahlung.arbeitszeitkonto
                          .gehaltsempfaenger.arbeitstage,
                      )
                    : (field.state.value ?? undefined)
                }
                onValueChange={(value) =>
                  field.handleChange(value as keyof Arbeitstage)
                }>
                <SelectTrigger className="md:flex hidden">
                  <SelectValue
                    onBlur={field.handleBlur}
                    placeholder={t(
                      'domain.mitarbeiter.create.steps.abwesenheiten.allgemein.form.arbeitstage.placeholder',
                    )}
                  />
                </SelectTrigger>
                <SelectContent>
                  {options.map((key) => (
                    <SelectItem key={key} value={key}>
                      {t(
                        `domain.mitarbeiter.create.steps.abwesenheiten.allgemein.form.arbeitstage.options.${key}`,
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Drawer open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
                <DrawerTrigger className="md:hidden flex" asChild>
                  <Button
                    variant={'outline-secondary'}
                    className="md:hidden flex flex-row justify-between gap-1 w-full text-neutrals-900 text-sm font-normal max-w-96 border-neutrals-200 hover:bg-neutrals-100"
                    role="combobox"
                    disabled={arbeitstageSelected}
                    type="button">
                    {field.state.value &&
                      t(
                        `domain.mitarbeiter.create.steps.abwesenheiten.allgemein.form.arbeitstage.options.${field.state.value}`,
                      )}
                    <FontAwesomeIcon
                      icon={faAngleDown}
                      className="h-4 w-4 ml-auto"
                    />
                  </Button>
                </DrawerTrigger>
                <DrawerContent className="flex flex-col gap-4 p-4">
                  <div className="font-semibold text-center">
                    {t(
                      `domain.mitarbeiter.create.steps.abwesenheiten.allgemein.form.arbeitstage.label`,
                    )}
                  </div>

                  <ScrollArea className="overflow-auto max-h-108">
                    <div className="flex flex-col gap-2">
                      {Object.keys(arbeitstage).map((key) => (
                        <div
                          className="flex justify-between items-center gap-2 not-last:border-b border-neutrals-200 pb-2"
                          key={key}
                          onClick={() => {
                            field.handleChange(key as keyof Arbeitstage);
                            setIsDrawerOpen(false);
                          }}>
                          <span>
                            {t(
                              `domain.mitarbeiter.create.steps.abwesenheiten.allgemein.form.arbeitstage.options.${key}`,
                            )}
                          </span>
                          {field.state.value === key && (
                            <FontAwesomeIcon
                              className="text-primary-blue-600 pr-2"
                              icon={faCheck}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </DrawerContent>
              </Drawer>
            </FieldBase>
          )}
        </form.AppField>
        {arbeitstageSelected && (
          <span className="text-neutrals-700 text-xs font-normal leading-4">
            {t(
              'domain.mitarbeiter.create.steps.abwesenheiten.allgemein.form.arbeitstage.notEditableDescription',
            )}
          </span>
        )}
      </div>
    </div>
  );
}
